<template>
  <main class="serve-invitation-page">
    <section v-if="loading" class="serve-invitation-state" role="status">
      <span class="spinner-border text-success" aria-hidden="true"></span>
      <p>Preparing your invitation…</p>
    </section>

    <section v-else-if="error" class="serve-invitation-state" role="alert">
      <div class="serve-invitation-icon is-error"><i class="material-icons">link_off</i></div>
      <p class="serve-invitation-eyebrow">Invitation unavailable</p>
      <h1>This invitation link can’t be used.</h1>
      <p>{{ error }}</p>
      <router-link to="/sign-in" class="serve-invitation-link">Go to sign in</router-link>
    </section>

    <section v-else-if="invitation" class="serve-invitation-card">
      <header class="serve-invitation-header">
        <div class="serve-invitation-icon"><i class="material-icons" aria-hidden="true">volunteer_activism</i></div>
        <p class="serve-invitation-eyebrow">A place to belong. A way to give.</p>
        <h1>You’re invited to serve.</h1>
        <p class="serve-invitation-location">{{ invitation.organization_name || invitation.location_name }}</p>
      </header>

      <div class="serve-invitation-body">
        <p class="serve-invitation-greeting">Hello {{ invitation.firstname }},</p>
        <p class="serve-invitation-copy">
          <strong>{{ invitation.inviter_name || 'A fellow volunteer' }}</strong> would be glad to serve alongside you
          at <strong>{{ invitation.location_name }}</strong>.
        </p>

        <section v-if="invitation.skills?.length" class="serve-skill-section" aria-label="Suggested serving roles">
          <p class="serve-invitation-eyebrow">Suggested for you</p>
          <div class="serve-skill-list">
            <div v-for="skill in invitation.skills" :key="skill.id" class="serve-skill-item">
              <i class="material-icons" aria-hidden="true">check_circle</i>
              <span><strong>{{ skill.name }}</strong><small v-if="skill.description">{{ skill.description }}</small></span>
            </div>
          </div>
        </section>

        <blockquote v-if="invitation.custom_message" class="serve-personal-note">
          <p class="serve-invitation-eyebrow">A note from {{ invitation.inviter_name || 'your friend' }}</p>
          <p>{{ invitation.custom_message }}</p>
        </blockquote>

        <div v-if="accepted" class="serve-accepted" role="status">
          <i class="material-icons" aria-hidden="true">task_alt</i>
          <div><strong>Thank you for saying yes.</strong><span>Your interest has been shared with the location team.</span></div>
        </div>
        <div v-else-if="invitation.already_registered" class="serve-existing-note">
          <i class="material-icons" aria-hidden="true">verified_user</i>
          <span>Your account is ready. Sign in with <strong>{{ invitation.email }}</strong> to accept this invitation.</span>
        </div>

        <section v-if="invitation.recipient_exists" class="serve-how-to" aria-label="How to request to serve in other areas">
          <p class="serve-invitation-eyebrow">You already have an account</p>
          <p class="serve-how-to-intro">To request to serve in other areas, including the ones suggested above, after you sign in:</p>
          <ol>
            <li>Choose <strong>{{ invitation.location_name }}</strong> if you are asked to pick a Location.</li>
            <li>Click the <strong>Serve in other Areas</strong> link on your volunteer menu.</li>
            <li>Select the skill(s) you are interested in, move them to the right-hand column and save.</li>
          </ol>
          <p class="serve-how-to-note">Your request is routed to the appropriate team administrators.</p>
        </section>

        <p v-if="actionError" class="serve-action-error" role="alert">{{ actionError }}</p>
        <div v-if="!accepted" class="serve-invitation-actions">
          <button v-if="isAuthenticated" class="serve-accept-button" type="button" :disabled="accepting" @click="acceptInvitation">
            <span v-if="accepting" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            <i v-else class="material-icons" aria-hidden="true">volunteer_activism</i>
            {{ accepting ? 'Saving…' : 'I’d like to serve' }}
          </button>
          <router-link v-else :to="signInTarget" class="serve-accept-button">
            <i class="material-icons" aria-hidden="true">login</i>
            Sign in to respond
          </router-link>
          <p>No pressure. You can decide what feels right for you.</p>
        </div>
      </div>
      <footer class="serve-invitation-footer">ChurchScheduling · {{ invitation.location_name }}</footer>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/config/api';

const route = useRoute();
const loading = ref(true);
const accepting = ref(false);
const accepted = ref(false);
const error = ref('');
const actionError = ref('');
const invitation = ref(null);
const token = computed(() => String(route.params.token || ''));
const isAuthenticated = computed(() => Boolean(localStorage.getItem('token')));
const signInTarget = computed(() => ({
  name: 'SignIn',
  query: { redirect: `/serve-invitation/${token.value}` },
}));

async function loadInvitation() {
  try {
    const response = await api.get(`/volunteer-invitations/token/${encodeURIComponent(token.value)}`);
    invitation.value = response.data?.data || null;
    if (!invitation.value) {
      error.value = 'Ask your friend to send you a new invitation.';
      return;
    }
    if (!invitation.value.recipient_exists) {
      window.__router__?.replace({ name: 'SignUp', query: { invite: token.value } });
    }
    accepted.value = invitation.value.already_accepted === true;
  } catch (requestError) {
    error.value = requestError.response?.data?.message || 'Ask your friend to send you a new invitation.';
  } finally {
    loading.value = false;
  }
}

async function acceptInvitation() {
  accepting.value = true;
  actionError.value = '';
  try {
    const response = await api.post('/volunteer-invitations/claim', { token: token.value });
    accepted.value = true;
    invitation.value.already_registered = true;
    actionError.value = '';
    if (response.data?.message && response.data?.success !== true) {
      actionError.value = response.data.message;
    }
  } catch (requestError) {
    actionError.value = requestError.response?.data?.message || 'We could not save your response. Please try again.';
  } finally {
    accepting.value = false;
  }
}

onMounted(loadInvitation);
</script>

<style scoped>
.serve-invitation-page {
  display: grid;
  min-height: 100vh;
  place-items: center;
  padding: 2rem 1rem;
  background: radial-gradient(ellipse at 50% 0%, #e7f3e9 0%, #f4f6f1 46%, #edf1ec 100%);
  color: #293d31;
}
.serve-invitation-card,.serve-invitation-state {
  width: min(100%, 620px);
  overflow: hidden;
  border: 1px solid #d9e6da;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 18px 48px rgba(31, 67, 45, 0.12);
}
.serve-invitation-header { padding: 2rem 2rem 1.7rem; background: #176b4d; color: #fff; text-align: center; }
.serve-invitation-icon { display: grid; width: 54px; height: 54px; margin: 0 auto 1rem; place-items: center; border: 1px solid rgba(255,255,255,.35); border-radius: 50%; background: rgba(255,255,255,.12); color: #fff; }
.serve-invitation-icon .material-icons { font-size: 27px; }
.serve-invitation-eyebrow { margin: 0 0 .45rem; color: #bfe3c9; font-size: .68rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.serve-invitation-header h1 { margin: 0; color: #fff; font-size: clamp(1.5rem, 5vw, 2rem); font-weight: 750; }
.serve-invitation-location { margin: .55rem 0 0; color: #e4f1e7; font-size: .9rem; }
.serve-invitation-body { padding: 1.8rem 2rem; }
.serve-invitation-greeting { margin: 0 0 .5rem; color: #365141; font-size: 1.05rem; font-weight: 700; }
.serve-invitation-copy { margin: 0 0 1.25rem; color: #526359; font-size: .94rem; line-height: 1.7; }
.serve-skill-section { margin: 1.2rem 0; padding: 1rem; border-left: 3px solid #70a67a; border-radius: 0 6px 6px 0; background: #f3f8f3; }
.serve-skill-section .serve-invitation-eyebrow,.serve-personal-note .serve-invitation-eyebrow { color: #527260; }
.serve-how-to { margin: 1.2rem 0; padding: 1rem; border-left: 3px solid #5b8bc0; border-radius: 0 6px 6px 0; background: #f4f7fb; color: #33475c; }
.serve-how-to .serve-invitation-eyebrow { color: #4a6584; }
.serve-how-to-intro { margin: 0 0 .5rem; font-size: .88rem; }
.serve-how-to ol { margin: 0 0 .5rem; padding-left: 1.2rem; font-size: .86rem; line-height: 1.7; }
.serve-how-to-note { margin: 0; color: #5b6f84; font-size: .78rem; }
.serve-skill-list { display: grid; gap: .65rem; }
.serve-skill-item { display: flex; align-items: flex-start; gap: .55rem; color: #3c5142; }
.serve-skill-item > .material-icons { margin-top: .07rem; color: #39845a; font-size: 18px; }
.serve-skill-item strong,.serve-skill-item small { display: block; }
.serve-skill-item strong { font-size: .88rem; }
.serve-skill-item small { margin-top: .12rem; color: #748179; font-size: .76rem; line-height: 1.45; }
.serve-personal-note { margin: 1.15rem 0; padding: 1rem 1.05rem; border: 1px solid #e4e9e3; border-radius: 6px; }
.serve-personal-note p:last-child { margin: 0; color: #4f5e54; font-size: .88rem; line-height: 1.65; white-space: pre-line; }
.serve-existing-note,.serve-accepted { display: flex; align-items: flex-start; gap: .65rem; margin: 1rem 0; padding: .85rem; border-radius: 6px; background: #f1f7f1; color: #53665a; font-size: .82rem; line-height: 1.5; }
.serve-existing-note .material-icons,.serve-accepted .material-icons { color: #378154; font-size: 20px; }
.serve-accepted strong,.serve-accepted span { display: block; }
.serve-accepted strong { margin-bottom: .2rem; color: #286b43; }
.serve-invitation-actions { margin-top: 1.4rem; text-align: center; }
.serve-accept-button { display: inline-flex; min-height: 46px; align-items: center; justify-content: center; gap: .5rem; padding: .7rem 1.15rem; border: 0; border-radius: 5px; background: #176b4d; color: #fff; font-size: .87rem; font-weight: 700; text-decoration: none; }
.serve-accept-button:hover:not(:disabled) { background: #11583e; color: #fff; }
.serve-accept-button:disabled { opacity: .65; }
.serve-accept-button .material-icons { font-size: 19px; }
.serve-invitation-actions > p { margin: .7rem 0 0; color: #7a877e; font-size: .75rem; }
.serve-action-error { color: #a34135; font-size: .82rem; text-align: center; }
.serve-invitation-footer { padding: .9rem 2rem; border-top: 1px solid #e8eee8; background: #fafcfa; color: #87938a; font-size: .7rem; text-align: center; }
.serve-invitation-state { display: flex; min-height: 250px; flex-direction: column; align-items: center; justify-content: center; padding: 2rem; text-align: center; }
.serve-invitation-state .serve-invitation-icon { background: #edf5ee; color: #176b4d; border-color: #dceade; }
.serve-invitation-icon.is-error { color: #a64a3d; background: #fbefed; border-color: #f1d8d4; }
.serve-invitation-state .serve-invitation-eyebrow { color: #76877a; }
.serve-invitation-state h1 { margin: 0 0 .5rem; font-size: 1.35rem; }
.serve-invitation-state > p:not(.serve-invitation-eyebrow) { color: #718077; font-size: .88rem; }
.serve-invitation-link { color: #28704b; font-weight: 700; }
@media (max-width: 575.98px) {
  .serve-invitation-page { padding: 1rem .6rem; }
  .serve-invitation-header { padding: 1.5rem 1.2rem; }
  .serve-invitation-body { padding: 1.3rem 1.15rem; }
}
</style>